import { useEffect, useState } from "react";
import { Brain, Plus, X } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  MEMORY_CATEGORIES,
  getPlanningMemory,
  addMemoryItem,
  removeMemoryItem,
} from "@/lib/planningMemory";
import { useTranslation } from "@/i18n";

// User-facing editor for the AI planning memory. The user is always in control:
// they can add, review and remove any remembered planning context (spec §18-20).
export default function PlanningMemorySettings() {
  const { t } = useTranslation();
  const [mem, setMem] = useState(() => getPlanningMemory());
  const [drafts, setDrafts] = useState({});

  useEffect(() => {
    const r = () => setMem(getPlanningMemory());
    window.addEventListener("chronicles:memory-changed", r);
    return () => window.removeEventListener("chronicles:memory-changed", r);
  }, []);

  const add = (cat) => {
    const text = (drafts[cat] || "").trim();
    if (!text) return;
    addMemoryItem(cat, text);
    setDrafts((d) => ({ ...d, [cat]: "" }));
  };

  const empty = MEMORY_CATEGORIES.every((c) => !(mem[c] && mem[c].length));

  return (
    <section data-testid="settings-planning-memory">
      <div className="flex items-center gap-2 mb-1">
        <Brain className="h-4 w-4 text-slate-700" />
        <h2 className="font-display text-lg font-semibold">{t("memory.settingsTitle")}</h2>
      </div>
      <p className="text-[12px] text-slate-500 leading-relaxed mb-3">{t("memory.settingsDesc")}</p>

      {empty ? (
        <p className="text-sm text-slate-500 rounded-xl border border-dashed border-border px-4 py-4" data-testid="memory-empty">
          {t("memory.empty")}
        </p>
      ) : null}

      <div className="space-y-5">
        {MEMORY_CATEGORIES.map((cat) => (
          <div key={cat} data-testid={`memory-cat-${cat}`}>
            <p className="text-[11px] uppercase tracking-[0.16em] text-slate-500 mb-2">
              {t(`memory.categories.${cat}`)}
            </p>
            <div className="space-y-2">
              {(mem[cat] || []).map((item) => (
                <div
                  key={item.id}
                  className="flex items-center gap-2 rounded-xl border border-border bg-card px-3 py-2.5"
                  data-testid={`memory-item-${item.id}`}
                >
                  <span className="min-w-0 flex-1 text-sm text-slate-800">{item.text}</span>
                  <button
                    onClick={() => removeMemoryItem(cat, item.id)}
                    className="h-7 w-7 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-700 shrink-0"
                    data-testid={`memory-remove-${item.id}`}
                    aria-label={t("memory.remove")}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2 mt-2">
              <Input
                value={drafts[cat] || ""}
                onChange={(e) => setDrafts((d) => ({ ...d, [cat]: e.target.value }))}
                placeholder={t("memory.addPlaceholder")}
                className="rounded-xl h-11 flex-1"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    add(cat);
                  }
                }}
                data-testid={`memory-input-${cat}`}
              />
              <Button
                variant="outline"
                className="rounded-xl h-11 px-3 border-slate-300 shrink-0 gap-1"
                onClick={() => add(cat)}
                disabled={!(drafts[cat] || "").trim()}
                data-testid={`memory-add-${cat}`}
              >
                <Plus className="h-4 w-4" />
                {t("memory.add")}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
